import React, { useMemo } from 'react';
import { Users, Building2 } from 'lucide-react';

const AVATAR_COLORS = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-green-500',
    'bg-teal-500',
    'bg-red-500',
    'bg-indigo-500',
];

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .map(w => w[0])
        .filter(Boolean)
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function getBarColor(total, avg) {
    if (avg === 0) return 'bg-green-500';
    const ratio = total / avg;
    if (ratio < 0.8) return 'bg-blue-500';
    if (ratio <= 1.2) return 'bg-green-500';
    if (ratio <= 1.5) return 'bg-amber-500';
    return 'bg-red-500';
}

function getBarLabel(total, avg) {
    if (avg === 0) return 'Balanced';
    const ratio = total / avg;
    if (ratio < 0.8) return 'Under';
    if (ratio <= 1.2) return 'Balanced';
    if (ratio <= 1.5) return 'Over';
    return 'Heavy';
}

/**
 * StagingRepBalancer -- Per-rep allocation cards with visual balance indicators.
 * Horizontal scrollable row used in the staging area for distributing
 * contacts/companies across sales reps.
 */
export default function StagingRepBalancer({ reps = [], onRepClick, activeRepId }) {
    const { maxContacts, avgContacts } = useMemo(() => {
        if (!reps.length) return { maxContacts: 0, avgContacts: 0 };
        const totals = reps.map(r => r.total_contacts || 0);
        return {
            maxContacts: Math.max(...totals, 1),
            avgContacts: totals.reduce((s, v) => s + v, 0) / totals.length,
        };
    }, [reps]);

    if (!reps.length) {
        return (
            <div className="flex items-center justify-center py-6 text-sm text-stone-400">
                <Users className="w-4 h-4 mr-2" />
                No reps assigned
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {/* Header + Legend */}
            <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    Rep Balance
                </h3>
                <div className="flex items-center gap-3 text-[10px] text-stone-500">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        Balanced
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                        Over
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                        Heavy
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                        Under
                    </span>
                </div>
            </div>

            {/* Scrollable card row */}
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {reps.map((rep, idx) => {
                    const isActive = activeRepId === rep.user_id;
                    const initials = getInitials(rep.name);
                    const colorClass = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                    const barWidth = maxContacts > 0
                        ? Math.max(4, ((rep.total_contacts || 0) / maxContacts) * 100)
                        : 4;
                    const barColor = getBarColor(rep.total_contacts || 0, avgContacts);
                    const barLabel = getBarLabel(rep.total_contacts || 0, avgContacts);

                    return (
                        <button
                            key={rep.user_id}
                            onClick={() => onRepClick?.(rep.user_id)}
                            className={`flex-shrink-0 w-48 rounded-lg border bg-white p-3 text-left transition-all hover:shadow-md ${
                                isActive
                                    ? 'border-blue-400 ring-2 ring-blue-400/40 shadow-md'
                                    : 'border-stone-200 hover:border-stone-300'
                            }`}
                        >
                            {/* Avatar + Name */}
                            <div className="flex items-center gap-2 mb-2">
                                <div
                                    className={`w-8 h-8 rounded-full ${colorClass} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
                                >
                                    {initials}
                                </div>
                                <span className="text-sm font-medium text-stone-800 truncate">
                                    {rep.name}
                                </span>
                            </div>

                            {/* Stat grid */}
                            <div className="grid grid-cols-[auto_1fr_1fr] gap-x-2 gap-y-0.5 text-[11px] mb-2">
                                {/* Header row */}
                                <span />
                                <span className="text-stone-400 flex items-center gap-0.5">
                                    <Building2 className="w-3 h-3" /> Co
                                </span>
                                <span className="text-stone-400 flex items-center gap-0.5">
                                    <Users className="w-3 h-3" /> Ct
                                </span>

                                {/* In CRM row */}
                                <span className="text-stone-500">In CRM</span>
                                <span className="text-stone-600 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {rep.crm_accounts ?? 0}
                                </span>
                                <span className="text-stone-600 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {rep.crm_contacts ?? 0}
                                </span>

                                {/* Pending row */}
                                <span className="text-green-600">+ Pending</span>
                                <span className="text-green-600 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {rep.pending_companies ?? 0}
                                </span>
                                <span className="text-green-600 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {rep.pending_contacts ?? 0}
                                </span>
                            </div>

                            {/* Total bar */}
                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <span
                                        className="text-xs font-bold text-stone-800"
                                        style={{ fontFamily: 'Manrope, sans-serif' }}
                                    >
                                        {rep.total_accounts ?? 0} co / {rep.total_contacts ?? 0} ct
                                    </span>
                                    <span className={`text-[10px] ${
                                        barLabel === 'Balanced' ? 'text-green-600' :
                                        barLabel === 'Over' ? 'text-amber-600' :
                                        barLabel === 'Heavy' ? 'text-red-600' :
                                        'text-blue-600'
                                    }`}>
                                        {barLabel}
                                    </span>
                                </div>
                                <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${barColor} rounded-full transition-all duration-300`}
                                        style={{ width: `${barWidth}%` }}
                                    />
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
